import Phaser from 'phaser';
import { TRUTH_FRAGMENTS, EPOCHS, THE_13_FALLEN } from '../data/GameConstants';

export default class LoreScene extends Phaser.Scene {
  constructor() {
    super({ key: 'LoreScene' });
    this.currentPage = 0;
  }

  create() {
    const { width, height } = this.cameras.main;
    this.cameras.main.setBackgroundColor('#0A0505');

    // Title
    this.add.text(width / 2, 40, 'THE LORE', {
      font: 'bold 36px Playfair Display',
      fill: '#FFD700'
    }).setOrigin(0.5);

    // Tab buttons
    const tabs = ['THE STORY', 'THE 10 TRUTHS', 'THE 13 FALLEN', 'THE EPOCHS'];
    const tabWidth = 150;
    const tabStartX = (width - tabs.length * tabWidth) / 2;

    this.tabButtons = [];
    tabs.forEach((tab, index) => {
      const x = tabStartX + index * tabWidth + tabWidth / 2;
      const button = this.add.text(x, 90, tab, {
        font: '16px Manrope',
        fill: index === 0 ? '#FFD700' : '#AAAAAA'
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });

      button.on('pointerdown', () => this.showTab(index));
      this.tabButtons.push(button);
    });

    // Content container
    this.contentContainer = this.add.container(0, 130);
    this.showTab(0);

    // Back button
    this.add.text(50, height - 40, '← BACK', {
      font: '20px Manrope',
      fill: '#FFFFFF'
    }).setInteractive({ useHandCursor: true })
      .on('pointerdown', () => this.scene.start('TitleScene'));

    this.cameras.main.fadeIn(500, 0, 0, 0);
  }

  showTab(index) {
    // Update tab highlighting
    this.tabButtons.forEach((btn, i) => {
      btn.setStyle({ fill: i === index ? '#FFD700' : '#AAAAAA' });
    });

    // Clear content
    this.contentContainer.removeAll(true);

    switch (index) {
      case 0:
        this.showStory();
        break;
      case 1:
        this.showTruths();
        break;
      case 2:
        this.showFallen();
        break;
      case 3:
        this.showEpochs();
        break;
    }
  }

  showStory() {
    const { width, height } = this.cameras.main;
    
    const storyText = `THE ANCIENT TRUTH

In the beginning, humanity existed as HUE-men — light-based beings possessing 
full access to the Ten Parts of Truth. This made them uncontrollable.

THE ARRIVAL

A technologically advanced species arrived from Nibiru, a planet 9× Earth's mass. 
They had blue copper-based blood, high-pressure physiology, and lifespans spanning 
thousands of years. Their agenda: modify humans into a subservient labor class.

THE KILLING OF TRUTH

In the original myth cycles, gods kill their father to become God.
In this reconstruction: they didn't kill a father — they killed the TRUTH.
They destroyed Earth's original civilization.
They scattered the "ten truths" to the ends of the Earth.
They reintroduced fragments as taboo knowledge to maintain control.

THE THREE RESETS

1. THE FIRE RESET — Plasma weapons devastated the world
2. THE FLOOD RESET — Waters rose, only the Ark survived  
3. THE IRON-TO-CLAY AGE — Spiritual knowledge declined, mechanical intelligence rose

THE CHOICE

Now, at Level 999, nuclear war is inevitable.
Only one remnant survives.

Will you restore the Ten Parts of Truth?
Or will you mine gold to save Nibiru?

The choice... is yours.`;

    const text = this.add.text(width / 2, 20, storyText, {
      font: '16px Manrope',
      fill: '#CCCCCC',
      lineSpacing: 8,
      align: 'center'
    }).setOrigin(0.5, 0);

    this.contentContainer.add(text);
  }

  showTruths() {
    const { width } = this.cameras.main;
    const fragments = Object.values(TRUTH_FRAGMENTS);
    
    const title = this.add.text(width / 2, 10, 'THE TEN PARTS OF TRUTH', {
      font: 'bold 24px Playfair Display',
      fill: '#FFD700'
    }).setOrigin(0.5);
    this.contentContainer.add(title);

    fragments.forEach((fragment, index) => {
      const y = 60 + index * 48;
      const x = width / 2 - 300;

      // Number
      const num = this.add.text(x, y, `${index + 1}.`, {
        font: 'bold 18px Manrope',
        fill: '#FFD700'
      });

      // Name with Hebrew
      const name = this.add.text(x + 30, y, `${fragment.name}`, {
        font: 'bold 16px Manrope',
        fill: '#FFFFFF'
      });

      // Hebrew
      const hebrew = this.add.text(x + 250, y, fragment.hebrew, {
        font: '18px serif',
        fill: '#D4AF37'
      });

      // Effect
      const effect = this.add.text(x + 350, y, fragment.effect, {
        font: '14px Manrope',
        fill: '#4CAF50'
      });

      // Description
      const desc = this.add.text(x + 30, y + 22, fragment.description, {
        font: '12px Manrope',
        fill: '#888888'
      });

      this.contentContainer.add([num, name, hebrew, effect, desc]);
    });
  }

  showFallen() {
    const { width } = this.cameras.main;
    const fallen = Object.values(THE_13_FALLEN);

    const title = this.add.text(width / 2, 10, 'THE 13 FALLEN', {
      font: 'bold 24px Playfair Display',
      fill: '#FFD700'
    }).setOrigin(0.5);
    this.contentContainer.add(title);

    // Corrupted
    const corruptedTitle = this.add.text(100, 50, '🔴 CORRUPTED SCEPTER BEARERS', {
      font: 'bold 16px Manrope',
      fill: '#FF6B6B'
    });
    this.contentContainer.add(corruptedTitle);

    let y = 80;
    fallen.filter(f => f.faction === 'corrupted').forEach(char => {
      const entry = this.add.text(120, y, `• ${char.name} (${char.origin}) - "${char.role}"`, {
        font: '14px Manrope',
        fill: '#CCCCCC'
      });
      this.contentContainer.add(entry);
      y += 25;
    });

    // Righteous
    const righteousTitle = this.add.text(100, y + 20, '🟢 RIGHTEOUS SCEPTER BEARERS', {
      font: 'bold 16px Manrope',
      fill: '#4CAF50'
    });
    this.contentContainer.add(righteousTitle);

    y += 50;
    fallen.filter(f => f.faction === 'righteous').forEach(char => {
      const entry = this.add.text(120, y, `• ${char.name} (${char.origin}) - "${char.role}"`, {
        font: '14px Manrope',
        fill: '#CCCCCC'
      });
      this.contentContainer.add(entry);
      y += 25;
    });

    // Neutral
    const neutralTitle = this.add.text(100, y + 20, '🐍 SERPENT STAFF BEARERS', {
      font: 'bold 16px Manrope',
      fill: '#9C27B0'
    });
    this.contentContainer.add(neutralTitle);

    y += 50;
    fallen.filter(f => f.faction === 'neutral').forEach(char => {
      const entry = this.add.text(120, y, `• ${char.name} (${char.origin}) - "${char.role}"`, {
        font: '14px Manrope',
        fill: '#CCCCCC'
      });
      this.contentContainer.add(entry);
      y += 25;
    });
  }

  showEpochs() {
    const { width } = this.cameras.main;
    const epochs = Object.values(EPOCHS);

    const title = this.add.text(width / 2, 10, 'THE FOUR EPOCHS', {
      font: 'bold 24px Playfair Display',
      fill: '#FFD700'
    }).setOrigin(0.5);
    this.contentContainer.add(title);

    epochs.forEach((epoch, index) => {
      const y = 60 + index * 100;
      const x = 100;

      const epochColors = ['#FFD700', '#FF5722', '#2196F3', '#795548'];

      // Epoch header
      const header = this.add.text(x, y, `EPOCH ${epoch.id}: ${epoch.name.toUpperCase()}`, {
        font: 'bold 18px Manrope',
        fill: epochColors[index]
      });

      // Time period
      const time = this.add.text(x + 400, y, epoch.timePeriod, {
        font: 'italic 14px Manrope',
        fill: '#888888'
      });

      // Level range
      const levels = this.add.text(x, y + 25, `Levels ${epoch.levelRange[0]} - ${epoch.levelRange[1]}`, {
        font: '14px Manrope',
        fill: '#AAAAAA'
      });

      // Description
      const desc = this.add.text(x, y + 45, epoch.description, {
        font: '14px Manrope',
        fill: '#CCCCCC'
      });

      this.contentContainer.add([header, time, levels, desc]);
    });
  }
}
